/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * Created by cmiller on 8/19/14.
 */

// import {Dyn} from '../dynDataSet/dynMiscellany';

export const wrapDynPlotShaper = function(Dyn) {

/**
 * Dyn.PlotShaper()
 * The model used by Dyn.PlotView to build a plot.
 * The Plot Shaper determines the shape of the plot when plot elements area added to our plotElement list.
 * The Plot Shaper references the data set, axes, and legend to calculate the dependent elements (case icons, adornments, etc).
 */
class PlotShaper {

    /**
     * @param initialElement
     * @param adornments
     * @param plotBounds {Dyn.Rect} - reference to rect owned by Dyn.PlotView
     * @param newNumCases
     * @param dataSet {Dyn.DataSet} - reference to data set used by Dyn.PlotView
     * @param animator {Dyn.plotAnimator} - reference, owned by Dyn.PlotView
     * @constructor
     */
    constructor( dataSet, adornments, plotBounds, initialElement=null, newNumCases=0 ) {

        this.dataSet = dataSet; // reference to data set for plot elements {Dyn.DataSet}

        // PlotShaper model
        this.plotElements = [];  // array of independent plot elements (Dyn.PlotElement)

        // PlotShaper dependent elements
        this.adornments = adornments; // reference to Dyn.PlotAdornments, owned by Dyn.PlotView
        this.plotBounds = plotBounds; // reference to Dyn.Rect, owned by Dyn.PlotView
        // this.plotAnimator = animator; // reference to Dyn.PlotAnimator, owned by Dyn.PlotView
        this.iconStyler = null;
        // WARNING: issue #1330, numCasesInDataSet not valid until plotView.updatePlotView() called, consider using this.dataSet.getNumCasesPreFilter() instead.
        this.numCasesInDataSet = 0; // cached length of Dyn.Attribute.cases[] arrays, updated by updateDataSize().
        this.numIconsPerCase = 1;	// normally 1, but 2+ allowed for certain plots where multiple attributes allowed on an axis
        this.bins = {
            xBins: [],	// bins formed by divisions/categories on X axis
            yBins: [],	// bins formed by divisions/categories on Y axis
            cells: [],	// grid of bins formed intersection of xNumBins and yNumBins
            maxCasesInCells: 0
        };
        this.measures = new Dyn.PlotMeasures( dataSet );
        this.sampleStats = new Dyn.SampleStats( dataSet, this );
        this.showLabelsOnStats = false; // default to labels off
        this.showSquaresOnLines = false; // if true, show squares on the selected regression line
        this.needsDefaultTitle = false; // if true, allow plot element to set default plot title

        if( initialElement instanceof Dyn.PlotElement ) {
            Dyn.assert( initialElement instanceof Dyn.PlotElement, "expecting a Dyn.PlotElement" );
            this.iconStyler = new Dyn.IconStyler();
            // check metadata for custom icon shape and size, otherwise calculate the (variable) default size.
            let customIconPath = dataSet.getCustomCaseIcon(),
                customIconSize = dataSet.getCustomIconSize(),
                paper = adornments.paper;
            if( customIconPath ) {
                this.iconStyler.setCustomCaseIcon( paper, customIconPath );
            }
            if( customIconSize > 0 ) {
                this.iconStyler.setCustomCaseIconSize( customIconSize );
            }

            this.iconStyler.setStyleCircle( newNumCases );
            this.addElement( initialElement );
        }
    }

    destroy() {
        // Destroying each plotElements
        this.plotElements.forEach(element => {
            element.destroy();
        });
        this.dataSet = null;
    }

    /**
     * Save the PlotShaper to a data object that can be JSONified
     * @returns {{}} - data to be used by restore()
     */
    save(withSummary) {
        let shaperModel = {};
        if( !(Dyn.plotUtil.isDrawOnly() || Dyn.plotUtil.isTuvaJrPlay()) ) { // don't save if only used for drawing
            shaperModel.iconStyler = this.iconStyler.save();
            shaperModel.showLabelsOnStats =Boolean( this.showLabelsOnStats );
            shaperModel.showSquaresOnLines = Boolean( this.showSquaresOnLines );
        }
        shaperModel.plotElements = this.plotElements.map( plotEl => plotEl.save());
        if( withSummary ) {
            // add optional extra data for authoring or lessons.
            shaperModel.summary = this.measures.exportMeasures();
        }
        return shaperModel;
    }

    /**
     * Change our PlotShaper to match the saved data.
     */
    restore( savedData, partialPlotState ) {
        "use strict";
        // var thisPlotShaper = this,
        // 	plotElem = null;
        if( savedData.iconStyler ) {
            this.iconStyler.restore( savedData.iconStyler, this.adornments.paper );
        }
        if( typeof savedData.showLabelsOnStats === 'boolean' ) {
            // only restore if explicitly on or off; old plots use current default
            this.showLabelsOnStats = savedData.showLabelsOnStats;
        }
        if( typeof savedData.showSquaresOnLines === 'boolean' ) {
            // only restore if explicitly on or off; old plots use current default
            this.showSquaresOnLines = savedData.showSquaresOnLines;
        }
        if( savedData.plotElements && Array.isArray( (savedData.plotElements) ) ) {
            this.removeElement( 'any', true ); // remove all elements
            // allow a restored plot element to examine other element data
            //      e.g. restore old Histogram with Count Sections vs with Percent of Sections.
            partialPlotState.plotElements = savedData.plotElements;
            savedData.plotElements.forEach( ( value ) => {
                let plotElem = Dyn.PlotElemHelper.restore( value, partialPlotState );
                if( plotElem ) {
                    this.addElement( plotElem );
                }
            } );
            if( !this.hasElement( Dyn.EPlotControl.kControlsShape ) ) { // fix for #1331
                Dyn.assert( this.hasElement( Dyn.EPlotControl.kControlsShape ), "WARNING: Constructing Dyn.MixupElement for restored plot that has no plot type" );
                this.addElement( new Dyn.MixupElement( Dyn ) );
                Dyn.assert( this.hasElement( Dyn.EPlotControl.kControlsShape ) );
            }
        }

        // if saved title, clear 'needsDefaultTitle' flag so custom plot title is preserved (even if an empty string)
        // if no saved title, make sure we change the plot title to the new default (e.g. change of language)
        // so that plot title is updated correctly in any case. #1389, #1390
        this.needsDefaultTitle = !Boolean( partialPlotState.hasSavedPlotTitle );
    }

    /** the plot has been reset (aka "mixUp"), we must clear any state */
    reset( newMixupElement, topMargin ) {
        this.addElement( newMixupElement, topMargin ); // the mixup element replaces all other elements automatically
        newMixupElement.reset();
        this.showSquaresOnLines = false;
        this.showLabelsOnStats = false;
    }

    toString() {
        "use strict";
        return '{PlotShaper}';
    }

    /**
     * hasElement() -- Does this plotShaper have an element with the given property? (e.g. element.controlsShape == true)
     * @param plotControlString {string} - string of type Dyn.EPlotControl
     * @returns {boolean}
     */
    hasElement( plotControlString ) {
        "use strict";
        return (this.findElement( plotControlString ) !== null);
    }

    /**
     * findElement() -- find and return the first Dyn.PlotElement that has the given property, or null.
     *
     * @param plotControlString {string} of type Dyn.EPlotControl
     * @returns {Dyn.PlotElement || null}
     */
    findElement( plotControlString ) {
        "use strict";
        let foundElement = this.plotElements.find( e => Boolean( e[plotControlString] ) );
        return foundElement ? foundElement : null;
    }

    /** return an array of all elements that have the given property */
    findAllElements( plotControlString ) {
        return this.plotElements.filter( e => Boolean( e[plotControlString] ) );
    }

    /**
     * add a Dyn.PlotElement to the plot. If the new plot element might change the plot type
     * and therefore the plot title, we pass in the view that shows the title, otherwise not necessary.
     * @param newPlotElement {Dyn.PlotElement} -- plot element like DotPlotElement, XRefLineElement, etc.
     * @param [topMargin] {Dyn.TopMarginView} -- optional title view
     */
    addElement( newPlotElement, topMargin ) {
        "use strict";

        // plot title change allowed now?
        if( newPlotElement[Dyn.EPlotControl.kControlsShape] ) {
            // we are about to change the plot type, do we need a new default title?
            // The actual title will change later when newPlotElement.calcAxis() calls this.setDefaultPlotTitle()
            let oldPlotTitleIsBlank = Boolean( topMargin && (topMargin.getTitle().length === 0) ),
                oldPlotTitleisDefault = this.isDefaultTitleForPlot( topMargin ? topMargin.getTitle() : "" );
            this.needsDefaultTitle = Boolean( oldPlotTitleIsBlank || oldPlotTitleisDefault );
        }

        // let new element examine existing plot elements, to remove or modify any
        // that are incompatible. Iterate backwards because we are modifying the array.
        // example: new LineGraphSingleElement replaces old DotPlotElement
        // example: new FreqBarElement replaces old BarOfSumsElement, modifies Count or Percent Elements if any.
        // example: new MixupElement replace *every* existing plot element to reset the plot
        for( var i = this.plotElements.length - 1; i >= 0; i -= 1 ) {
            if( newPlotElement.replaces( this.plotElements[i], this.adornments ) ) {
                var removedElements = this.plotElements.splice( i, 1 );
                removedElements[0].destroy(); // clean up Dyn.PlotElement and let garbage collection handle the rest
            }
        }

        // add new element
        Dyn.assert( newPlotElement instanceof Dyn.PlotElement );
        newPlotElement.plotShaper = this;
        newPlotElement.debugValidate();
        this.plotElements.push( newPlotElement );

        // sort elements if more than one
        function comparePlotElementByPriority( a, b ) {
            // usual <0, 0, >0 sorting for ascending priority values
            return a.priority - b.priority;
        }

        this.plotElements.sort( comparePlotElementByPriority )
        //console.log( "plotShaper.addElement.sort:");
        //this.plotElements.forEach( el => console.log( "sorted element: "+el.toString()));
    }

    /**
     * remove one or more elements from our list of elements.
     * @param plotControlString {string} -- control type in Dyn.EPlotControl
     * @param removeAll -- if true remove all elements.
     * @returns {{}}
     */
    removeElement( plotControlString, removeAll ) {
        "use strict";
        var i, j = this.plotElements.length, e, savedControlFlags = {};

        for( i = j - 1; i >= 0; i -= 1 ) {
            e = this.plotElements[i];

            if( e[plotControlString] || removeAll ) {
                var removedElement = this.plotElements.splice( i, 1 )[0];
                saveControlFlags( removedElement );
                removedElement.destroy(); // clean up Dyn.PlotElement and let garbage collection handle the rest
            }
        }
        return savedControlFlags;

        // save Dyn.EPlotControl boolean flags for caller
        function saveControlFlags( plotElem ) {
            for( var param in plotElem ) {
                if( /^controls/.test( param ) && typeof plotElem[param] === "boolean" ) {
                    savedControlFlags[param] = plotElem[param];
                }
            }
        }
    }


    /**
     * Toggle a boolean control in a Dyn.PlotElement in the plot (e.g. Row Percents on/off)
     * or Add a control to a Dyn.PlotElement in the plot (e.g. add a Circle Annotation).
     * Assumes each plot element type either toggles or adds, not both.
     * @param primaryControlID
     * @param toggleControlID
     * @returns {Dyn.PlotElem} -- element that was modified
     */
    modifyToggleElementControl( primaryControlID, options ) {
        // find and update the element
        var plotElem = this.findElement( primaryControlID );
        if( options?.toggleControlID ) {
            plotElem.toggleControl( options.toggleControlID, options );
        } else if( options?.addControlID ) {
            plotElem.addControl( options.addControlID, options );
        } else {
            Dyn.assert( false, "modifyToggleElementControl() no element found for control: "+primaryControlID );
        }
        return plotElem;
    }

    /**
     * Given the X and Y axis contents, is a new primary plot element needed?
     * Used when a new attribute is dropped on the axis.
     * @param xAxisView
     * @param yAxisView
     * @returns {Dyn.PlotElement} new plot element or null
     */
    getNewPlotElementForAxes( xAxisView, yAxisView ) {
        "use strict";
        var numAxesUsed = (xAxisView.isEmptyOfAttributes() ? 0 : 1) + (yAxisView.isEmptyOfAttributes() ? 0 : 1),
            oldShapeElement = this.findElement( Dyn.EPlotControl.kControlsShape ),
            newShapeElement = null;

        if( (numAxesUsed > 0) && oldShapeElement[Dyn.EPlotControl.kControlsMixup] ) {
            // if dropping an attribute on a mixup, make a dot plot
            newShapeElement = new Dyn.DotPlotElement( Dyn );
        }
        if( (numAxesUsed === 0) && oldShapeElement[Dyn.EPlotControl.kControlsDotPlot] ) {
            // if removing the last attribute on a dot plot, mix up the plot
            // this special case is so user can undo "drop attribute to make a dot plot" above.
            newShapeElement = new Dyn.MixupElement( Dyn );
        }
        // else don't change the plot type,
        // in case user wants to drop another attribute and make the same kind of plot

        return newShapeElement;
    }

    /**
     * Update Axes after plot element change, allows each element to modify axis to match plot element.
     * @param ioWhatChanged {{}} parameters of what has changed in plot
     * @param subViewRef -- references to Dyn.AxisView(s)
     */
    updateAxes( ioWhatChanged, subViewRef ) {
        "use strict";
        //Dyn.time( 'plotShaper:updateAxes', 'Start' );
        var thisShaper = this,
            numCasesPreFilter = this.dataSet.getNumCasesPreFilter();
            //selectedAttribute = this.dataSet.getSelectedAttribute();

        function isAxisSpecificOrAllAxisReset( whatChanged, thisAxisID ) {
            // has attribute data or metaData changed significantly? On this axis or all axes?
            let noAxisDirection = typeof (ioWhatChanged.isHorizontal) !== 'boolean',
                noAxisID = typeof (ioWhatChanged.axisID) !== 'string',
                isSignificantChange = (ioWhatChanged.dataChanged || ioWhatChanged.attributeReset),
                isAllAxisChange = isSignificantChange && noAxisID,
                isThisAxisChange = isSignificantChange && (ioWhatChanged.axisID === thisAxisID);
            Dyn.assert( noAxisID === noAxisDirection, "want both or neither parameters" );
            return (isAllAxisChange || isThisAxisChange);
        }

        // clear any axis customizations (that no longer apply)
        if( isAxisSpecificOrAllAxisReset( ioWhatChanged, 'x' ) ) {
            subViewRef.xAxisView.axisCustomizationsReset();
        }
        if( isAxisSpecificOrAllAxisReset( ioWhatChanged, 'y' ) ) {
            subViewRef.yAxisView.axisCustomizationsReset();
        }
        if( subViewRef.y2AxisView && isAxisSpecificOrAllAxisReset( ioWhatChanged, 'y2' ) ) {
            subViewRef.y2AxisView.axisCustomizationsReset();
        }
        // update state of axis settings to "update pending"
        subViewRef.xAxisView.setStatsPending( true );
        subViewRef.yAxisView.setStatsPending( true );
        subViewRef.y2AxisView && subViewRef.y2AxisView.setStatsPending( true );

        // allow each plot element to modify axes
        this.plotElements.forEach( ( value ) => {
            if( value.controlsShape ) {
                thisShaper.numIconsPerCase = value.calcNumIconsPerCase( ioWhatChanged, subViewRef );
            }
            value.calcAxis( ioWhatChanged, subViewRef );
            if( value.controlsShape ) { // controlsShape elements are allowed to set/clear missing values now
                value.calcMissingValueCases( numCasesPreFilter, subViewRef.xAxisView, subViewRef.yAxisView, subViewRef.y2AxisView );
                value.calcPlotSpecificRange( numCasesPreFilter, subViewRef );
            }
        } );

        // hack the DataSet.setSelectedAttribute() behavior so that it does not override line plot order
        // this.dataSet.selectedAttributeAlsoOrders = this.displayOrderBy() && (! this.isCustomOrderForLines());
        // this.dataSet.selectedAttributeAlsoOrders = !(this.isCustomOrderForLines()

        // allow axes and margins to update once based on calcAxis() changes
        //	this allows for single efficient response to changes from multiple plot elements
        //	or response based on the absence of a particular plot element.
        subViewRef.xAxisView.postCalcAxisUpdate();
        subViewRef.yAxisView.postCalcAxisUpdate();
        subViewRef.y2AxisView && subViewRef.y2AxisView.postCalcAxisUpdate();
        subViewRef.topMargin.postCalcAxisUpdate();
        subViewRef.rightMargin.postCalcAxisUpdate();

        // update measure data for axes
        this.measures.setAttributeIDs(
            subViewRef.xAxisView.allAttributes.getAttributesArray(),
            subViewRef.yAxisView.allAttributes.getAttributesArray(),
            subViewRef.rightMargin.isColorAxis() ? [ subViewRef.rightMargin.getColorAttribute() ] : [] );
        //Dyn.time( 'plotShaper:updateAxes', 'End' );

        // update the sample stats data for axes
        this.sampleStats.setAttributeIDs(
            subViewRef.xAxisView.allAttributes.getAttributesArray(),
            subViewRef.yAxisView.allAttributes.getAttributesArray() );

        // check to make sure plot elements updated axis state to no longer pending
        subViewRef.xAxisView.checkStatsPending();
        subViewRef.yAxisView.checkStatsPending();
        subViewRef.y2AxisView && subViewRef.y2AxisView.checkStatsPending();
    }

    /**
     * updateDataSize() - Update the ioCasePositions array to match the new number of case icons
     * needed by the plot (if either the data or the plot has changed).
     *
     * Handles 1+ case icons per data case, see updateAxes() and this.numIconsPerCase.
     *
     * @param numCasesPreFilter -- numer of cases in attributes of our data set, after filtering
     * @param ioCasePositions -- array of case positions, one per case icon (holds data to create/animate case icons)
     * @returns {Boolean} true if array is changed
     */
    updateDataSize( numCasesPreFilter, ioCasePositions ) {
        "use strict";
        //Dyn.time( 'updateDataSize', 'Start' );
        Dyn.assert( numCasesPreFilter >= 0 && ioCasePositions instanceof Array, 'invalid args' );

        var oldNumIcons = ioCasePositions.length,
            curNumIcons = oldNumIcons,
            newNumIcons = numCasesPreFilter * this.numIconsPerCase,// 1+ case icons per data case.
            tChanged = (oldNumIcons !== newNumIcons),
            caseIndex;
        this.numCasesInDataSet = numCasesPreFilter; // save for use by PlotElements.

        if( curNumIcons < newNumIcons ) {
            // expand casePositions array
            for( curNumIcons; curNumIcons < newNumIcons; ++curNumIcons ) {
                caseIndex = (ioCasePositions.length) % numCasesPreFilter;
                ioCasePositions.push( { x: 0, y: 0, caseIndex: caseIndex } );  // caller should set position
            }
        }
        if( curNumIcons > newNumIcons ) {
            // shrink casePositions array
            ioCasePositions.length = newNumIcons;
        }

        Dyn.assert( ioCasePositions.length === (this.numCasesInDataSet * this.numIconsPerCase) );
        //if( tChanged )
        //	console.log( "plotShaper.updateDataSize() from " +oldNumIcons +" to "+ newNumIcons +" icons" );
        //Dyn.time( 'updateDataSize', 'End' );
        return tChanged;
    }

    /**
     * Update the number and position of the bin rectangles from the axes.
     * Updates:
     *  this.bins.xBins[] - array of bins on X axis, default is 1 bin, more if multiple categories on axis.
     *  this.bins.yBins[] - array of bins on X axis, default is 1 bin, more if multiple categories on axis.
     *  this.bins.cells[[ - array of cells formed by the intersection of X and Y Bins
     *      (cells.length===xBins.length*yBins.length)
     */
    updateBinPositions( xAxisView, yAxisView, ioAdornments ) {
        "use strict";

        updateBinListOnAxis( this.bins.xBins, xAxisView, this.plotBounds );
        updateBinListOnAxis( this.bins.yBins, yAxisView, this.plotBounds );
        updateCellList( this.bins.cells, this.bins.xBins, this.bins.yBins, this.plotBounds );

        function updateBinListOnAxis( binList, axisView, overallBounds ) {
            Dyn.assert( binList && axisView );
            var tNewLength = axisView.getNumSeparations(),
                i, b;

            // resize the bin array
            if( binList.length > tNewLength ) binList.length = tNewLength;
            for( i = binList.length; i < tNewLength; i++ ) {
                binList.push( new Dyn.mathUtil.Rect( 0, 0, 0, 0 ) );   // add empty rectangle
            }

            // update the bin boundary values
            for( i = 0; i < tNewLength; i++ ) {
                b = binList[i];
                b.copyRect( overallBounds ); // default bin covers entire plot interior
                if( tNewLength > 1 ) {
                    // if and only if we have multiple bins on this axis do we set the location of each
                    axisView.updateRectToSeparation1D( i, b );
                }
            }
            Dyn.assert( binList.length === tNewLength );
        }

        function updateCellList( cells, xBins, yBins, overallBounds ) {
            Dyn.assert( cells );
            var numXBins = xBins.length,
                numYBins = yBins.length,
                newNumCells = (numXBins * numYBins),
                cellIndex, cellRect,
                yBinIndex, xBinIndex, yBinRect, xBinRect;

            // resize the bin array
            if( cells.length > newNumCells ) {
                cells.forEach( cell => cell.removeDebugRect() );  // undo addDebugRect() if used
                cells.length = newNumCells;
            }
            for( cellIndex = cells.length; cellIndex < newNumCells; cellIndex++ ) {
                cells.push( new Dyn.mathUtil.Rect( 0, 0, 0, 0 ) );   // add empty rectangle
            }
            Dyn.assert( cells.length === newNumCells );

            // for each cell, assign rows and columns
            cellIndex = 0;
            for( yBinIndex = 0; yBinIndex < numYBins; ++yBinIndex ) {
                yBinRect = yBins[yBinIndex];
                for( xBinIndex = 0; xBinIndex < numXBins; ++xBinIndex ) {
                    xBinRect = xBins[xBinIndex];

                    // set cell rect as intersection of its x and y bin bounds
                    cellRect = cells[cellIndex];
                    cellRect.x = xBinRect.x;
                    cellRect.y = yBinRect.y;
                    cellRect.width = xBinRect.width;
                    cellRect.height = yBinRect.height;
                    cellRect.xBinIndex = xBinIndex;
                    cellRect.yBinIndex = yBinIndex;
                    cellRect.cells = []; // see populateCellsWithCases()
                    cellIndex++;
                }
            }
            Dyn.assert( cellIndex === newNumCells, 'cell miscount' );
            if( Dyn.debugViewRects ) {
                // if debugging view rectangles, add a visible rectangle
                cells.forEach( cell => cell.addDebugRect( ioAdornments.paper, "#9c9" ) );
            }
        }
    }

    /**
     * Return the index of the cell at the intersection of the given X and Y bins.
     * e.g. this.bins.cells[0] for this.bins.xBins[0] and this.bins.yBins[0]
     */
    calcCellIndex( xBinIndex, yBinIndex ) {
        "use strict";
        return ((yBinIndex * this.bins.xBins.length) + xBinIndex); // match updateCellList() order
    }

    /**
     * updateIconStyle()
     * @param ioCasePositions
     * @param xAxis {Dyn.AxisView}
     * @param yAxis {Dyn.AxisView}
     * @param y2Axis {Dyn.AxisView|null}
     * @param rightMargin {Dyn.MarginView}
     * @returns {boolean}
     */
    updateIconStyle( ioCasePositions, xAxis, yAxis, y2Axis, rightMargin ) {
        "use strict";

        // allow the plot element(s) to update the icon style (e.g. circle for dot plot, rect for bar chart)
        $.each( this.plotElements, function( index, value ) {
            //console.log("ploShaper calcPosition of "+value.kUndoAction);
            value.calcIconStyle( ioCasePositions, xAxis, yAxis );
        } );

        // change in icon style or icon size (based on number of cases) can change margin of numeric axis
        let newAxisMargin = this.iconStyler.getDesiredAxisMargin(),
            changed = Boolean( (newAxisMargin !== xAxis.margin) ||
                (newAxisMargin !== yAxis.margin) ||
                (y2Axis && (newAxisMargin !== y2Axis.margin)) );
        xAxis.setMargin( newAxisMargin );
        yAxis.setMargin( newAxisMargin );
        y2Axis && y2Axis.setMargin( newAxisMargin );

        // update the "hide border" setting if isIncompleteAxis setting changes, for box and dot plots
        let isIncompleteAxis = xAxis.isIncompleteAxis() || yAxis.isIncompleteAxis() || rightMargin.isIncompleteAxis();
        this.iconStyler.setHideBorder( isIncompleteAxis );

        return changed;
    }

    /**
     * updatePositionsAndAdornments()
     * Updates the case positions and other case-specific data (common to all plot types).
     * Updates the adornments (visual elements specific to each plot element).
     * @param ioCasePositions
     * @param ioAdornments
     * @param subViewRef {*} -- see PlotView.getSubViewRef()
     * @param noAnimation {boolean}
     */
    updatePositionsAndAdornments( ioCasePositions, ioAdornments, subViewRef, noAnimation ) {
        "use strict";
        //Dyn.time( 'updatePositionsAndAdornments', 'Start' );

        // get the new view position of bins formed from X or Y axis divisions/categories.
        this.updateBinPositions( subViewRef.xAxisView, subViewRef.yAxisView, ioAdornments );
        // now ask axis views to draw the lines for each bin on other axis
        subViewRef.xAxisView.updateBinLines( this.bins );
        subViewRef.yAxisView.updateBinLines( this.bins );
        this.measures.setBinNames( // bin names can be affected by calls to calcPlotSpecificRange() and postCalcAxisUpdate()
            subViewRef.xAxisView.allAttributes.stats.categories,
            subViewRef.yAxisView.allAttributes.stats.categories,
            subViewRef.rightMargin.getColorAttribute() );

        // then get the position of missing-value cases, so plot elements can use MV location
        if( this.dataSet.getSelectedAttribute() !== null) {
            this.iconStyler.updateMissingValuePosition( // position above legend Top-Left, plus small offset
                this.plotBounds.right() + Dyn.plotUtil.missingCasePosX,
                this.plotBounds.top() - Dyn.plotUtil.missingCasePosY );
        } else {
            this.iconStyler.updateMissingValuePosition( // no attribute in legend, so move to Top-Right
                Dyn.plotView.rightMargin.bounds.right() - Dyn.plotUtil.kMinAxisThickness,
                this.plotBounds.top() - Dyn.plotUtil.missingCasePosY );
        }

        // now update each plot element, and the case icon positions and adornments controlled by those elements
        this.plotElements.forEach( ( element, index ) => {
            //console.log("ploShaper calcPosition of "+value.kUndoAction);
            element.calcPosition( ioCasePositions, subViewRef.xAxisView, subViewRef.yAxisView, ioAdornments, subViewRef.topMargin );
            element.calcAdornments( ioAdornments.getAdornmentsArray( element.primaryType ), ioCasePositions, subViewRef.xAxisView, subViewRef.yAxisView, noAnimation );
        } );
        this.plotElements.forEach( ( element ) => {
            if( element.postCalcPositionUpdate ) {
                element.postCalcPositionUpdate( ioCasePositions, ioAdornments, subViewRef, noAnimation );
            }
        } );
        Dyn.assert( this.bins.cells.length < 0 || this.bins.cells[0].cases, "plot cells are missing case order" );

        var dividersElement = this.findElement( Dyn.EPlotControl.kControlsDividers ),
            histogramElement = this.findElement( Dyn.EPlotControl.kControlsHistogram );
        this.measures.setDividerBinNames( dividersElement ? dividersElement.getDivisionNames() : [] );
        this.measures.setHistogramBinNames( histogramElement ? histogramElement.getBinNames() : [] );
        this.measures.setMeasureNames();
        this.measures.publishUpdates();

        // Sample stats
        this.sampleStats.publishUpdates();
        //Dyn.time( 'updatePositionsAndAdornments', 'End' );
    }

    /**
     * Update Axes after plot element change, allows each element to modify axis to match plot element.
     * @param ioWhatChanged
     * @param subViewRef
     * @param axisData
     * @returns {boolean} -- true if handled by a Dyn.PlotElement
     */
    handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
        "use strict";
        var handled = false;
        $.each( this.plotElements, function( index, value ) {
            if( !handled ) {
                // let one plot element handle axis button click
                handled = value.handleAxisButtonClick( ioWhatChanged, subViewRef, axisData );
            }
        } );
        return handled;
    }

    /**
     * Get data for sonifying the current plot.
     * @returns {*}
     */
    getPlotSonificationData( noteSequence ) {
        // allow each plot element to modify or add to the sound data.
        this.plotElements.forEach( ( plotElement ) => {
            if( plotElement.getPlotSonificationData ) {
                let plotSoundData = plotElement.getPlotSonificationData();
                noteSequence.types.push( plotSoundData );
            }
        } );
    }

    static numAdornmentLayers = 1; // default 0th layer, setAdornmentLayer() can add layers 1+ on top
    debugLayerAdornments = false; // show console messages for layerAdornments?

    /**
     * re-layer plot element adornments, by bringing all 'toFront()' in order
     *
     * Note: needed when adornments are created/destroyed in an unusual order,
     * or to recover after toFront() is used to make a particular element visible.
     *
     * See also PlotView.layerVisuals()
     */
    layerAdornments() {
        // bring the plot element adornments to front from first to last,
        //   according to plot element sort order (primary sort),
        //   and adornment array order (secondary sort)
        //   then put layer 1+ adornments above the default layer 0 adornments
        for( let i=0; i<PlotShaper.numAdornmentLayers; i++ ) {
            this.plotElements.forEach( el =>
                el.layerAdornments( this.adornments.getAdornmentsArray( el.primaryType ),  i ));
        }
    }

    /**
     * Set the layer number for one or more adornments (which are Raphael/SVG plot elements).
     * See PlotShaper.layerAdornments() and PlotElement.layerAdornments(), which actually change the layering.
     * @param layerNum {number} - an integer 0+, 0 being the default layer, 1=above layer 0, 2=above layer 1, etc.
     * @param adornments - the Raphael/SVG plot elements to set (a paper.rect(), paper.text(), paper.set() ,etc)
     * @return boolean - return true if any layers have changed
     */
    setAdornmentLayer( layerNum, ...adornments ) {
        Dyn.assert( layerNum >= 0 && layerNum === Math.round( layerNum ), "Adornment Layer is not an integer 0+: ",layerNum );
        // increase number of layers if necessary, for this.layerAdornments()
        if( layerNum >= PlotShaper.numAdornmentLayers ) {
            PlotShaper.numAdornmentLayers = layerNum+1; // increase number of layers (1+ is second layer)
            if( PlotShaper.debugLayerAdornments ) {
                Dyn.debugging && console.log( "adornmentToLayer is adding new layers, now:", PlotShaper.numAdornmentLayers );
            }
        }
        // set the Raphael plot element to use the new layer
        let changed = false;
        for (const adornment of adornments) {
            let oldLayerNum = adornment.data("layerNum");
            adornment.data( "layerNum", layerNum ); // can be a set or rect or text element, etc
            changed = changed || (layerNum !== oldLayerNum);
        }
        return changed;
    }

    /**
     * De-select any selected adornments.
     * @param exceptThisPlotElement -- except adornments owned by this plot element.
     */
    clearSelectedAdornments( exceptThisPlotElement ) {
        "use strict";
        this.plotElements.forEach( plotElement => {
            if( plotElement !== exceptThisPlotElement ) {
                plotElement.clearSelectedAdornments();
            }
        } );
    }

    /**
     * Caller is swapping the axes.
     * Do any necessary changes to plot elements.
     * Note that most plot elements don't need any special changes.
     */
    swapAxes() {
        // find plot elements that have a swapAxis() method (otherwise they don't need special handling)
        let elementsToSwap = this.plotElements.filter( p => p.swapAxis ),
            elementsToRestore = []
        elementsToSwap.forEach( oldPlotElement => {
            elementsToRestore.push( oldPlotElement.swapAxis()); // save data for restoreSwapped()
            this.removeElement( oldPlotElement.primaryType ); // remove the old plot element
        });
        elementsToRestore.forEach( ( [ oldControlType, newControlType, savedData ] ) => {
            // restore the plot element on the other axis
            let newPlotElement = Dyn.PlotElemHelper.restoreSwapped( oldControlType, newControlType, savedData );
            this.addElement( newPlotElement );
        });
    }

    /**
     * displayOrderBy() -- Does this plotShaper have an element that allows the plot cases to be ordered by an attribute?
     * E.g. true for all as we are making the change to make it visible all the time.
     * @returns {boolean}
     */
    displayOrderBy() {
        "use strict";
        var shapingElement = this.findElement( Dyn.EPlotControl.kControlsCaseOrder );
        // return Boolean( shapingElement && shapingElement.displayOrderBy());
        return Boolean( shapingElement && shapingElement.displayOrderBy() && this.dataSet.getOrderByAttribute() );
    }

    /**
     * allowCustomOrderBy() -- Does this plotShaper have an element that allows the plot cases to be order by an attribute?
     * E.g. false for pie chart, stacked and stretched bar chart as it does grouping based on order by.
     */
    allowCustomOrderBy() {
        var shapingElement = this.findElement( Dyn.EPlotControl.kControlsCaseOrder );
        return Boolean( shapingElement && shapingElement.allowCustomOrderBy() );
    }

    /**
     * allowLineOrderBy() -- Does this plotShaper have an element that allows the lines to be ordered by an attribute?
     * E.g. true for line plot, but false for all the others.
     * @returns {boolean}
     */
    allowLineOrderBy() {
        "use strict";
        var shapingElement = this.findElement( Dyn.EPlotControl.kControlsLineOrder );
        return Boolean( shapingElement && shapingElement.allowLineOrderBy() );
    }

    /**
     * hasCustomOrderBy() -- Does this plotShaper have an element with an active "order by" attribute?
     * @returns {boolean}
     */
    hasCustomOrderBy() {
        "use strict";
        var shapingElement = this.findElement( Dyn.EPlotControl.kControlsCaseOrder );
        return Boolean( shapingElement && shapingElement.hasCustomOrderBy() );
    }

    /**
     * Returns true if the custom order by is to be applied to line order, not the default case order.
     * @returns {boolean}
     */
    isCustomOrderForLines() {
        "use strict";
        return Boolean( this.hasElement( Dyn.EPlotControl.kControlsLineGraph ) );
    }

    /**
     * allowCustomDotSize() -- Does this plotShaper have an element that allows custom dot sizes?
     * True if there is a plot element that uses a dot or custom case icon, false
     * for plot elements that control pie slices or data-sized rectangles (Bar, Histogram, etc).
     */
    allowCustomDotSize() {
        switch( this.iconStyler.getCurrentIconStyle() ) {
            case Dyn.EIconStyle.kCircle:
            case Dyn.EIconStyle.kCustomIcon:
            case Dyn.EIconStyle.kVariableIcon:
                return true;
            default:
                return false;
        }
    }

    /**
     * We are about to add a new plot element to the plot, should the axis customizations
     * be reset? (e.g. user-adjusted numeric plot range)
     * TRUE: for most large plot changes like switching from Dot Plot to Map Plot
     * FALSE: for smaller plot changes like adding a reference line or grid or switch between Boxplot types.
     * @param newPlotElement
     * @returns {boolean}
     */
    wantAttributeResetOnAddElement( newPlotElement ) {
        let wantAttributeReset = Boolean( newPlotElement[Dyn.EPlotControl.kControlsShape] ), // if new plot element is a Shaper (Dot, Line, Box, but not Ref Line, Mean, etc)\
            existingShaperElement = this.findElement( Dyn.EPlotControl.kControlsShape );
        if( existingShaperElement && existingShaperElement.wantAttributeResetOnAddElement ) // if existing plot shaper has an override
            wantAttributeReset = existingShaperElement.wantAttributeResetOnAddElement( newPlotElement ); // get plot-specific override
        return wantAttributeReset;
    }

    /**
     * Set the view mode for any plot elements, which typically means hide or show any controls.
     * @param mode
     */
    setViewMode( mode ) {
        this.plotElements.forEach( element => {
            element.setViewMode( mode )
        } );
    }

    /** Get the accumulated margin size in view pixels caused by Counts, percents, other text in margin of each cell */
    getCellMargin() {
        var margin = { x: 0, y: 0 };
        this.plotElements.forEach( element => {
            if( element.getCellMargin ) {
                let m = element.getCellMargin();
                margin.x += m.x;
                margin.y += m.y;
            }
        } );
        return margin;
    }

    /** show or hide the visible labels on some plot elements. */
    showHideLabelsOnStats( wantVisibleLabels ) {
        return this.showLabelsOnStats = Boolean( wantVisibleLabels );
    }

    /** Show or hide squares on Least Squares Lines, Movable Lines, or similar plot element.
     * @param wantSquares {boolean} - turn squares on if true, off if false
     * @param [plotElementToUse] - optional element to show squares on, else search for one.
     */
    showHideSquaresOnLines( wantSquares, plotElementToUse ) {
        // record that show squares is on
        this.showSquaresOnLines = Boolean( wantSquares );

        // mark which element is showing squares, if any
        let elementsCanShowSquares = this.plotElements.filter( el => el[Dyn.EPlotControl.kControlsShowSquares] ),
            elementToShowSquares = null;
        if( this.showSquaresOnLines ) {
            // find the element that will show squares, searching for:
            // 1. valid plotElementToUse
            // 2. or any element with a selected line
            // 3. or any element that can show squares
            Dyn.assert( (!plotElementToUse) || plotElementToUse[Dyn.EPlotControl.kControlsShowSquares] );
            elementToShowSquares = elementsCanShowSquares.find( el => (el === plotElementToUse) )
                || elementsCanShowSquares.find( el => el.canShowSquares( true ) )
                || elementsCanShowSquares.find( el => el.canShowSquares( false ) );
        }
        // turn of show squares on all elements, except for the one where we want them showing, if any
        elementsCanShowSquares.forEach( el => {
            el.setShowSquares( elementToShowSquares && (el === elementToShowSquares) );
        } );
        // console.log( "showHideSquaresOnLines", this.showSquaresOnLines,
        //     elementToShowSquares && elementToShowSquares.primaryType,
        //     elementToShowSquares && elementToShowSquares.showSquaresLineIndex );
    }

    /** get the default title for the current plot, from the "shaper" plot element (Dyn.DotPlotElement, etc) */
    isDefaultTitleForPlot( titleString ) {
        // find the shaper" plot element (Dyn.DotPlotElement, etc). which has a default title
        let shaperElement = this.plotElements.find( element => typeof element[Dyn.EPlotControl.kControlsShape] );
        // if no shaper, return true if title is blank, to allow a new plot title to be set.
        return Boolean( shaperElement ? shaperElement.isDefaultTitleForPlot( titleString ) : (titleString.length === 0) );
    }

    /**
     * Given a default title for the plot, use it for the plot title, if needed.
     * Otherwise do nothing (preserve the existing user-modified title).
     * Plot elements like BoxPlotElement should call this to set their default plot title.
     * See also this.addElement(), this.restore()
     * @param topMargin
     * @param defaultTitle
     * @param overrideUserTitle -- optional parameter, if true replace user title with default
     */
    setDefaultPlotTitle( topMargin, defaultTitle, overrideUserTitle ) {
        Dyn.assert( topMargin );
        if( topMargin && (this.needsDefaultTitle || overrideUserTitle) ) {
            const isDefaultTitle = true;
            Dyn.assert( defaultTitle, "new plot is missing a default title" );
            topMargin.setTitle( defaultTitle || "", true, isDefaultTitle );
            this.needsDefaultTitle = false;
        }
    }

    /**
     * Does our plot have a plot element that has an adornment selected matching the value?
     * @param tableSetID {string} -- setID of the measure passed by the PlotElement to SummaryView
     * @param adornmentID {string} -- id of the measure passed by the PlotElement to SummaryView
     * @return {boolean} - TRUE if there is a matching Adornment that is selected.
     */
    isAdornmentSelected( tableSetID, adornmentID ) {
        return Boolean( this.plotElements.find(
            // find the Dyn.PlotElement that has an optional isAdornmentSelected() function that returns true
            el => ((el.isAdornmentSelected) && el.isAdornmentSelected( tableSetID, adornmentID )) ) );
    }

    /** select an adornment by value, e.g. when clicking on the associated cell in the summary view */
    selectAdornmentByValue( data ) {
        let changed = false;
        this.plotElements.forEach( el => {
            if( el.selectAdornment && el.selectAdornment( -1, data ) ) {
                changed = true;
            }
        } );
        return changed;
    }

    /**
     * Prepare the plot elements for a drag; this call is necessary
     * for hover over/hover out on certain plot elements that interact with
     * the map drag modes. Issue #1532, #1531
     * @param isDragStart
     */
    prepareElementsForDrag( isDragStart ) {
        this.plotElements.forEach( el => {
            if( el.prepareElementsForDrag ) {
                el.prepareElementsForDrag( isDragStart );
            }
        } );
    }

    /**
     * Does the current plot want a context menu visible in the title?
     * @returns {boolean}
     */
    wantPlotContextMenu() {
        // true if any one of the plot elements want a context menu
        let plotElementThatWantsMenu = this.plotElements.find( el =>
            el.contextMenuOptions // function exists
            && el.contextMenuOptions()); // function returns non-null object
        return Boolean(plotElementThatWantsMenu);
    }

    /**
     * Return null or context menu options for the current plot
     * @returns {boolean|{wantContextMenu: boolean, menuID: *, menuToolTip: string}|*}
     */
    contextMenuOptions() {
        let plotElementThatWantsMenu = this.plotElements.find( el =>
            el.contextMenuOptions // function exists
            && el.contextMenuOptions()); // function returns non-null object
        return plotElementThatWantsMenu?.contextMenuOptions();
    }

    /** Handle a click on the plot title's context menu */
    handleContextMenuClick( event, buttonElement ) {
        // find a plot element that has a context menu handler, if any.
        let handler = null,
            plotElement = null;
        this.plotElements.find( (el) => {
            handler = el.getContextMenuClickHandler();
            plotElement = el;
            return Boolean(handler);
        });
        // call static handler, if any. It may add/remove/change plot elements.
        if( handler ) {
            handler( event, buttonElement, plotElement );
        }
    }

    /**
     * If the color of a plot section has changed, update the things that depend on that color.
     * Needed to solve an order dependency: count and percents colors are calculated first,
     * then case color next, but the count/percent in-section color depends on the case color.
     * @param caseFillColor {Dyn.colorUtil.SVG_Color}
     * @param sectionIndex
     * @param cellIndex
     */
    updateSectionColor( caseFillColor, cellIndex, sectionIndex ) {
        let cell = this.bins.cells[cellIndex], // each cell has a plot (e.g. 2 cells === two pie charts in the plot)
            sections = cell.pieSections ? cell.pieSections : cell.barSections, // Array of Dyn.PlotSection instances
            section = sections && sections[sectionIndex];
        if( section && section.updateColors( caseFillColor, Dyn.plotUtil.caseBackgroundMinBrightness )) {
            // text color has changed; find and update all text elements overlaying this section of the Bar/Pie
            //console.log( cellIndex, sectionIndex, caseFillColor.colorString, caseFillColor.b255, section.textColor );
            this.plotElements.filter( ( el ) => el.updateSectionTextColor ).forEach( ( plotElement ) => {
                let elementAdornments = this.adornments.getAdornmentsArray( plotElement.primaryType );
                plotElement.updateSectionTextColor( elementAdornments, cellIndex, sectionIndex, section.textColor );
            } );
        }
    }

    /** return true if the plot type wants an attribute selection click to change the order-by attribute */
    orderByOnSelect( attributeID ) {
        return ! this.isCustomOrderForLines();
    }

    /** return true if the plot type wants an attribute selection click to change the bubble-size-by attribute */
    bubbleByOnSelect( attributeID ) {
        return this.hasElement( Dyn.EPlotControl.kControlsBubbles )
            && this.dataSet.getAttributeByID(attributeID)?.isNumeric();
    }

} // end class PlotShaper
Dyn.PlotShaper = PlotShaper;

} // end wrapDynPlotShaper
